import React from 'react';

import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReliasTraining, ReliasTrainingStatus } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { Button, LightChip } from '@uplift/ui-v2';

import { openInNewTab } from '../../../helpers';
import { useSharedStyles } from '../../../styles';
import { getTrainingMetadata, RELIAS_TRAINING_LAUNCH_URL, TrainingCadence } from './constants';

export type TrainingRowProps = {
  training: ReliasTraining;
};

export const TrainingRow: React.VFC<TrainingRowProps> = ({ training }) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const { classes: sharedClasses } = useSharedStyles();
  const metadata = getTrainingMetadata(training);
  const isCompleted = training.status === ReliasTrainingStatus.completed;

  let launchUrl = RELIAS_TRAINING_LAUNCH_URL;

  if (training.enrollmentId) {
    launchUrl = `https://betterhelp.training.reliaslearning.com/Learning/CourseViewer.aspx?id=${training.enrollmentId}`;
  }

  const handleLaunch = () => openInNewTab(launchUrl);

  return (
    <Box className={sharedClasses.progressSummaryStepWithChip}>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h5">{metadata?.label ?? training.courseTitle}</Typography>
          {metadata && (
            <LightChip
              size="small"
              label={metadata.cadence === TrainingCadence.annual
                ? l10n.dashboard.actionItems.requiredTrainings.annual
                : l10n.dashboard.actionItems.requiredTrainings.oneTime}
            />
          )}
        </Box>
        {metadata && (
          <Typography variant="body2" color="textSecondary">
            {l10n.dashboard.actionItems.requiredTrainings.estimatedTime(metadata.estimatedMinutes)}
          </Typography>
        )}
        {isCompleted && training.completedAt && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              {l10n.dashboard.actionItems.requiredTrainings.completedOn(
                CalendarDateService.date(training.completedAt, DateFormats.ISO_DATE).tz(timezone).format('MMM D, YYYY')
              )}
            </Typography>
          </Box>
        )}
      </Box>
      {!isCompleted && (
        <Button variant="contained" color="secondary" onClick={handleLaunch}>
          {training.status === ReliasTrainingStatus.inProgress
            ? l10n.dashboard.actionItems.requiredTrainings.continueCta
            : l10n.dashboard.actionItems.requiredTrainings.startCta}
        </Button>
      )}
    </Box>
  );
};
